import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaTag } from 'react-icons/fa';
import { buildAuthRequiredRedirectUrl } from '../Account/profileFormUtils';
import { formatCouponOfferLabel } from './cartCouponUtils';
import CartCouponPickerModal from './CartCouponPickerModal';

/**
 * @param {{
 *   cartTotal: number,
 *   coupons: import('./cartCouponUtils').NormalizedCoupon[],
 *   appliedCoupon: import('./cartCouponUtils').NormalizedCoupon | null,
 *   status: 'guest' | 'loading' | 'loaded' | 'error',
 *   error: string,
 *   codeInput: string,
 *   setCodeInput: (v: string) => void,
 *   codeError: string,
 *   setCodeError: (v: string) => void,
 *   onBrowseApply: (coupon: import('./cartCouponUtils').NormalizedCoupon) => void,
 *   onApplyCode: () => boolean,
 *   onRemove: () => void,
 *   onRetry: () => void,
 * }} props
 */
const CartCouponSection = ({
  cartTotal,
  coupons,
  appliedCoupon,
  status,
  error,
  codeInput,
  setCodeInput,
  codeError,
  setCodeError,
  onBrowseApply,
  onApplyCode,
  onRemove,
  onRetry,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const signInUrl = buildAuthRequiredRedirectUrl('/cart');
  const isSignedIn = status !== 'guest';
  const showEntry = isSignedIn && status !== 'loading' && !appliedCoupon;
  const canOpenPicker = status === 'loaded';

  return (
    <>
      <div className="cart-coupon cart-summary-panel cart-summary-panel--coupon">
        <div className="cart-coupon__label-row">
          <div className="cart-coupon__label">
            <FaTag aria-hidden /> Coupon
          </div>
          {showEntry && (
            <button
              type="button"
              className="cart-coupon__browse-link"
              onClick={() => setPickerOpen(true)}
              disabled={!canOpenPicker}
            >
              View available coupons
            </button>
          )}
        </div>

        {status === 'guest' && (
          <div className="cart-coupon__guest">
            <p>Sign in to view and apply coupons.</p>
            <Link to={signInUrl} className="cart-coupon__sign-in">
              Sign in
            </Link>
          </div>
        )}

        {status === 'loading' && (
          <p className="cart-coupon__loading" aria-live="polite">
            Loading coupons…
          </p>
        )}

        {isSignedIn && status !== 'loading' && appliedCoupon && (
          <div className="cart-coupon__applied">
            <span>
              <strong>{appliedCoupon.code}</strong>
              {appliedCoupon.description
                ? ` — ${appliedCoupon.description}`
                : ` — ${formatCouponOfferLabel(appliedCoupon)} applied`}
            </span>
            <button type="button" onClick={onRemove} className="cart-coupon__remove">
              Remove
            </button>
          </div>
        )}

        {showEntry && (
          <>
            <div className="cart-coupon__input-row">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value);
                  setCodeError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onApplyCode();
                }}
                aria-label="Coupon code"
              />
              <button type="button" onClick={() => onApplyCode()} className="cart-coupon__apply-btn">
                Apply
              </button>
            </div>
            {status === 'loaded' && coupons.length === 0 && (
              <p className="cart-coupon__hint">No coupons available right now.</p>
            )}
          </>
        )}

        {status === 'error' && error && (
          <div className="cart-coupon__error-row">
            <p className="cart-coupon__error">{error}</p>
            <button type="button" className="cart-coupon__retry" onClick={onRetry}>
              Try again
            </button>
          </div>
        )}
        {codeError ? <p className="cart-coupon__error">{codeError}</p> : null}
      </div>

      <CartCouponPickerModal
        open={pickerOpen && status === 'loaded'}
        onClose={() => setPickerOpen(false)}
        coupons={coupons}
        cartTotal={cartTotal}
        onApply={onBrowseApply}
      />
    </>
  );
};

export default CartCouponSection;
